import { useState } from 'react';

import { Checkbox } from '@/components/form/Checkbox';
import { StyledDatePicker } from '@/components/form/StyledDatePicker';
import { Loader } from '@/components/Loader';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useWizardFooterConfig,
  WizardModalFooter,
  WizardStepper,
} from '@/components/wizards';
import {
  ExportFormat,
  useExportReport,
  useValidateReport,
  ValidateReportError,
} from '@/hooks/useReportExport';
import { RiskSelectionStep } from '@/sections/export/components/RiskSelectionStep';
import { useReportConfigForm } from '@/sections/export/hooks/useReportConfigForm';
import { useRiskSelection } from '@/sections/export/hooks/useRiskSelection';
import { useAuth } from '@/state/auth';
import { RiskStatus } from '@/types';

const getTodayISO = (): string => new Date().toISOString().split('T')[0];

// Radix Select forbids empty-string item values, so "" (= don't include) is
// represented by this sentinel.
const NONE_VALUE = '__none__';

// Sentinel executive_summary_path understood by the backend: the report job
// generates the executive summary from the report's findings via an LLM.
const AUTO_EXECUTIVE_SUMMARY = 'auto';

export interface GenerateReportWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type StepKey = 'risks' | 'details' | 'review';

const STEPS: StepKey[] = ['risks', 'details', 'review'];

const STEP_TITLES: Record<StepKey, string> = {
  risks: 'Select risks',
  details: 'Report details',
  review: 'Review & generate',
};

const STEP_DESCRIPTIONS: Record<StepKey, string> = {
  risks: 'Choose which risks this report should cover.',
  details: 'Configure the report title, format, dates, and included sections.',
  review: 'Confirm the selection and report configuration, then generate.',
};

/**
 * GenerateReportWizard — a Hannibal-style multi-step Dialog that replaces the
 * old report dropdown + modal. It generates a pentest PDF/ZIP report with
 * in-wizard risk selection.
 *
 * Externally controlled (like LaunchHannibalDialog): the parent owns `open` and
 * `onOpenChange` and renders its own trigger. Praetorian-only — returns `null`
 * for non-Praetorian users.
 */
export function GenerateReportWizard({
  open,
  onOpenChange,
}: GenerateReportWizardProps) {
  const { isPraetorianUser } = useAuth();

  if (!isPraetorianUser) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onOpenChange(false)}>
      <DialogContent className="max-w-3xl">
        <GenerateReportForm key={String(open)} onOpenChange={onOpenChange} />
      </DialogContent>
    </Dialog>
  );
}

function GenerateReportForm({
  onOpenChange,
}: {
  onOpenChange: (open: boolean) => void;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentStep = STEPS[currentIndex];

  const selection = useRiskSelection();
  const {
    current,
    handleFieldChange,
    persistFormValues,
    fileVariants,
    isDataLoading,
  } = useReportConfigForm();

  const [validationErrors, setValidationErrors] = useState<
    ValidateReportError[]
  >([]);

  const { mutateAsync: validateReport, status: validateStatus } =
    useValidateReport();
  const { mutateAsync: exportReport } = useExportReport();

  const isValidating = validateStatus === 'pending';

  // 'details'/'review' need the report-config data; gate Next until it's loaded.
  // 'risks' is navigable once the date-range scope (if any) has fully resolved —
  // blocking while `selection.isResolving` ensures the report never validates or
  // exports against a partially-paged (capped) key set. See useRiskSelection.
  const canGoNext =
    currentStep === 'risks'
      ? !selection.isResolving && !selection.isLoadingOptions
      : !isDataLoading && !isValidating && !selection.isResolving;

  const handleGenerate = async () => {
    setValidationErrors([]);

    try {
      const result = await validateReport({
        risk_keys: selection.resolvedRiskKeys,
        status_filter: [RiskStatus.Opened],
        executive_summary_path: current.executiveSummaryPath || undefined,
        narratives_path: current.narrativesPath || undefined,
        group_by: current.groupBy,
      });

      if (!result.valid) {
        setValidationErrors(
          result.errors.filter(
            e =>
              e.type === 'missing_definition' || e.type === 'missing_phase_tags'
          )
        );
        return;
      }

      // Validation passed — persist config, start export, and close.
      persistFormValues(false);
      onOpenChange(false);
      await exportReport({
        risk_keys: selection.resolvedRiskKeys,
        status_filter: [RiskStatus.Opened],
        config: {
          title: current.title,
          client_name: current.clientName,
          sow: current.sow,
          target: current.target,
          start_date: current.startDate,
          end_date: current.endDate,
          report_date: getTodayISO(),
          draft: current.draft,
          retest: current.retest,
          version: current.version,
          footer: current.footer,
          confidential_label: current.confidentialLabel,
        },
        executive_summary_path: current.executiveSummaryPath || undefined,
        narratives_path: current.narrativesPath || undefined,
        appendix_path: current.appendixPath || undefined,
        export_format: current.exportFormat,
        group_by: current.groupBy,
      });
    } catch {
      // Error toast is handled by useExportReport / useValidateReport.
    }
  };

  const footerConfig = useWizardFooterConfig({
    canGoNext,
    canGoBack: currentIndex > 0,
    isPending: isValidating,
    isLastStep: currentIndex === STEPS.length - 1,
    onNext: () => setCurrentIndex(i => Math.min(i + 1, STEPS.length - 1)),
    onBack: () => setCurrentIndex(i => Math.max(i - 1, 0)),
    onCancel: () => onOpenChange(false),
    onSubmit: handleGenerate,
    labels: { submit: 'Generate Report', submitting: 'Generating...' },
  });

  const hasValidationErrors = validationErrors.length > 0;

  return (
    <>
      <DialogHeader>
        <DialogTitle>Generate Report</DialogTitle>
        <DialogDescription>{STEP_DESCRIPTIONS[currentStep]}</DialogDescription>
      </DialogHeader>

      <WizardStepper
        steps={STEPS.map(s => ({ id: s, title: STEP_TITLES[s] }))}
        currentStep={currentIndex + 1}
        variant="dots"
      />

      <div className="scrollbar-dark max-h-[600px] min-h-[420px] overflow-auto">
        {currentStep === 'risks' && <RiskSelectionStep selection={selection} />}

        {currentStep === 'details' &&
          (isDataLoading ? (
            <div className="flex flex-col gap-4">
              <Loader isLoading className="h-[60px] w-full" />
              <Loader isLoading className="h-[60px] w-full" />
              <Loader isLoading className="h-[60px] w-full" />
            </div>
          ) : (
            <ReportDetailsStep
              current={current}
              fileVariants={fileVariants}
              handleFieldChange={handleFieldChange}
            />
          ))}

        {currentStep === 'review' && (
          <ReviewStep
            current={current}
            selection={selection}
            validationErrors={validationErrors}
            hasValidationErrors={hasValidationErrors}
          />
        )}
      </div>

      <WizardModalFooter config={footerConfig} />
    </>
  );
}

type ReportConfigState = ReturnType<typeof useReportConfigForm>;

function ReportDetailsStep({
  current,
  fileVariants,
  handleFieldChange,
}: {
  current: ReportConfigState['current'];
  fileVariants: ReportConfigState['fileVariants'];
  handleFieldChange: ReportConfigState['handleFieldChange'];
}) {
  return (
    <div className="flex flex-col gap-5">
      {/* Report Title */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="report-title">Report Title</Label>
        <Input
          id="report-title"
          type="text"
          value={current.title}
          onChange={e => handleFieldChange('title', e.target.value)}
          placeholder="Client Penetration Test"
        />
      </div>

      {/* Format toggle */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="report-format">Format</Label>
        <Select
          value={current.exportFormat}
          onValueChange={value =>
            handleFieldChange(
              'exportFormat',
              value as typeof current.exportFormat
            )
          }
        >
          <SelectTrigger id="report-format" aria-label="Format">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ExportFormat.PDF}>PDF</SelectItem>
            <SelectItem value={ExportFormat.ZIP}>ZIP</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Group Findings By */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="report-group-by">Group Findings By</Label>
        <Select
          value={current.groupBy}
          onValueChange={value => handleFieldChange('groupBy', value)}
        >
          <SelectTrigger id="report-group-by" aria-label="Group Findings By">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="attack_surface">Attack Surface</SelectItem>
            <SelectItem value="tag">Tags (Phase)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Client Name + Target */}
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="report-client-name">Client Name</Label>
          <Input
            id="report-client-name"
            type="text"
            value={current.clientName}
            onChange={e => handleFieldChange('clientName', e.target.value)}
            placeholder="Client Inc"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="report-target">Target</Label>
          <Input
            id="report-target"
            type="text"
            value={current.target}
            onChange={e => handleFieldChange('target', e.target.value)}
            placeholder="example.com"
          />
        </div>
      </div>

      {/* Engagement Start / End dates (display-only on the report) */}
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="report-start-date">Start Date</Label>
          <StyledDatePicker
            name="report-start-date"
            value={current.startDate}
            onChange={value => handleFieldChange('startDate', value)}
            max={current.endDate || undefined}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="report-end-date">End Date</Label>
          <StyledDatePicker
            name="report-end-date"
            value={current.endDate}
            onChange={value => handleFieldChange('endDate', value)}
            min={current.startDate || undefined}
          />
        </div>
      </div>
      <p className="text-chariot-text-secondary -mt-3.5 text-xs">
        Engagement period shown on the report. Defaults to the customer&apos;s
        subscription term.
      </p>

      {/* Draft + Retest */}
      <div className="flex flex-col">
        <div className="flex items-center gap-3 py-2.5">
          <Checkbox
            name="draft"
            label="Draft"
            value={current.draft ? 'true' : 'false'}
            onChange={() => handleFieldChange('draft', !current.draft)}
            showTick
          />
          <span className="text-chariot-text-secondary text-xs">
            Adds a DRAFT watermark to the generated report
          </span>
        </div>
        <div className="flex items-center gap-3 py-2.5">
          <Checkbox
            name="retest"
            label="Retest"
            value={current.retest ? 'true' : 'false'}
            onChange={() => handleFieldChange('retest', !current.retest)}
            showTick
          />
          <span className="text-chariot-text-secondary text-xs">
            Include retest status badges and sections in the report
          </span>
        </div>
      </div>

      {/* Executive Summary file select */}
      <ReportFileSelect
        label="Executive Summary"
        value={current.executiveSummaryPath}
        options={fileVariants.executiveSummary}
        extraOptions={[
          {
            value: AUTO_EXECUTIVE_SUMMARY,
            label: 'Auto-generate from findings (AI)',
          },
        ]}
        onChange={value => handleFieldChange('executiveSummaryPath', value)}
      />

      {/* Narratives file select */}
      <ReportFileSelect
        label="Narratives"
        value={current.narrativesPath}
        options={fileVariants.narratives}
        onChange={value => handleFieldChange('narrativesPath', value)}
      />

      {/* Appendix file select */}
      <ReportFileSelect
        label="Appendix"
        value={current.appendixPath}
        options={fileVariants.appendix}
        onChange={value => handleFieldChange('appendixPath', value)}
      />
    </div>
  );
}

function ReviewStep({
  current,
  selection,
  validationErrors,
  hasValidationErrors,
}: {
  current: ReportConfigState['current'];
  selection: ReturnType<typeof useRiskSelection>;
  validationErrors: ValidateReportError[];
  hasValidationErrors: boolean;
}) {
  const coverageLabel = (() => {
    if (selection.mode === 'all') return 'All open risks';
    const count = selection.resolvedRiskKeys?.length ?? 0;
    if (selection.isResolving) return 'Resolving matching risks…';
    if (count === 0) return 'All open risks';
    return `${count} risk${count === 1 ? '' : 's'}`;
  })();

  const formatLabel = current.exportFormat === ExportFormat.ZIP ? 'ZIP' : 'PDF';
  const groupByLabel =
    current.groupBy === 'tag' ? 'Tags (Phase)' : 'Attack Surface';

  return (
    <div className="flex flex-col gap-5">
      {hasValidationErrors && (
        <div className="border-chariot-status-critical bg-chariot-status-critical/5 rounded-md border px-4 py-3">
          <p className="text-chariot-status-critical mb-2 text-sm font-semibold">
            Validation errors — please resolve before generating:
          </p>
          {validationErrors.some(e => e.type === 'missing_definition') && (
            <div>
              <p className="text-chariot-text-primary mb-1 text-sm">
                Missing definitions for the following risks:
              </p>
              <ul className="list-disc pl-5 text-sm">
                {validationErrors
                  .filter(e => e.type === 'missing_definition')
                  .map(err => (
                    <li
                      key={err.risk_key}
                      className="text-chariot-text-secondary"
                    >
                      {err.risk_name}
                    </li>
                  ))}
              </ul>
            </div>
          )}
          {validationErrors.some(e => e.type === 'missing_phase_tags') && (
            <div>
              <p className="text-chariot-text-primary mb-1 text-sm">
                The following findings are missing phase tags. Add a{' '}
                <code className="text-xs">phase_*</code> tag to each before
                exporting with tag-based grouping:
              </p>
              <ul className="list-disc pl-5 text-sm">
                {validationErrors
                  .filter(e => e.type === 'missing_phase_tags')
                  .map(err => (
                    <li
                      key={err.risk_key}
                      className="text-chariot-text-secondary"
                    >
                      {err.risk_name}
                    </li>
                  ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <dl className="divide-chariot-stroke border-chariot-stroke flex flex-col divide-y rounded-lg border">
        <ReviewRow label="Risks covered" value={coverageLabel} />
        <ReviewRow label="Report title" value={current.title || '—'} />
        <ReviewRow label="Format" value={formatLabel} />
        <ReviewRow label="Group findings by" value={groupByLabel} />
        <ReviewRow label="Client name" value={current.clientName || '—'} />
        <ReviewRow label="Target" value={current.target || '—'} />
        <ReviewRow
          label="Engagement period"
          value={
            current.startDate || current.endDate
              ? `${current.startDate || '—'} → ${current.endDate || '—'}`
              : '—'
          }
        />
        <ReviewRow
          label="Draft watermark"
          value={current.draft ? 'Yes' : 'No'}
        />
      </dl>
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-2.5">
      <dt className="text-chariot-text-secondary text-sm">{label}</dt>
      <dd className="text-chariot-text-primary truncate text-right text-sm font-medium">
        {value}
      </dd>
    </div>
  );
}

function ReportFileSelect({
  label,
  value,
  options,
  extraOptions = [],
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  /** Permanent non-file options rendered between (None) and the file list. */
  extraOptions?: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={`report-file-${label}`}>{label}</Label>
      <Select
        value={value || NONE_VALUE}
        onValueChange={v => onChange(v === NONE_VALUE ? '' : v)}
      >
        <SelectTrigger id={`report-file-${label}`} aria-label={label}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE_VALUE}>(None)</SelectItem>
          {extraOptions.map(o => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
          {options.map(f => (
            <SelectItem key={f} value={f}>
              {f}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
